// START: GitHub Copilot Generated Code
import { createListenerMiddleware, TypedStartListening } from "@reduxjs/toolkit";

import { LogVerbose } from "../../services/logUtils";
import { loadPageContentsAsync } from "../features/contentSlice";

// NOTE: setLanguage is NOT imported here to avoid a circular dependency:
// store.ts → contentMiddleware.ts → languageSlice.ts → languageService.ts → store.ts
// Instead we match on the action type string directly.

import type { RootState, AppDispatch } from '../store';

/**
 * Content Middleware
 * 
 * Automatically loads page contents when the language changes.
 * This middleware listens for setLanguage actions and dispatches
 * loadPageContentsAsync if content for the new language doesn't exist.
 * 
 * Benefits:
 * - Decouples content loading from UI components
 * - Components don't need useEffect for content loading
 * - Single source of truth for language-content synchronization
 * - Prevents duplicate loads with smart caching check
 * 
 * DRY Principle: Centralized loading logic
 * OWASP Security: Validates state before dispatching
 * 
 * Note: createListenerMiddleware is called without type parameters to avoid a circular
 * reference (store.ts → contentMiddleware → store.ts). Instead, startListening is cast
 * to AppStartListening which carries the proper RootState and AppDispatch types.
 */
export const contentMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
const startListening = contentMiddleware.startListening as AppStartListening;

startListening({
    // Compare previous and current state so the middleware only fires when the language
    // reducer actually accepted the change. If an unsupported language was dispatched the
    // reducer leaves the state untouched, currentState === previousState, and we skip.
    predicate: (action, currentState, previousState) =>
        action.type === 'language/setLanguage' &&
        currentState.language.language !== previousState.language.language,
    effect: async (action, listenerApi) => {
        const state = listenerApi.getState();
        // Read the committed language from state rather than from the action payload so we
        // always work with the validated, reducer-approved value.
        const newLanguage = state.language.language;

        // Validate language is provided and is a string (defensive guard)
        if (!newLanguage || typeof newLanguage !== 'string') {
            LogVerbose('Language is undefined or invalid, skipping content load');
            return;
        }

        // START: GitHub Copilot Generated Code
        // Check apiLoadedLanguages (not pageContentsByLanguage) so that pre-seeded local
        // fallback content does not prevent a fresh API fetch on language switch.
        // OWASP Security: newLanguage is validated by isSupportedLanguage() in the reducer
        const hasApiContent = state.content.apiLoadedLanguages.includes(newLanguage);
        // END: GitHub Copilot Generated Code
        const isLoading = state.content.loadingLanguages.includes(newLanguage);

        // Only load if API content doesn't exist and isn't already loading
        if (!hasApiContent && !isLoading) {
            LogVerbose(`Middleware: Auto-loading page contents for language: ${newLanguage}`);
            await listenerApi.dispatch(loadPageContentsAsync(newLanguage));
        } else if (hasApiContent) {
            LogVerbose(`Middleware: Content already cached for language: ${newLanguage}`);
        } else {
            LogVerbose(`Middleware: Content already loading for language: ${newLanguage}`);
            // Wait briefly for the in-flight content request to finish so secondary
            // prefetch remains lower priority than page content.
            let attempts = 0;
            while (attempts < 50) {
                await listenerApi.delay(100);
                const currentState = listenerApi.getState();
                const stillLoading = currentState.content.loadingLanguages.includes(newLanguage);
                if (!stillLoading) {
                    break;
                }
                attempts += 1;
            }
        }

        // Background prefetch lower-priority language resources after page content.
        LogVerbose(`Middleware: Triggering background prefetch of GetCareNow and Health Blog for language: ${newLanguage}`);
        const { loadGetCareNowDetails, loadHealthBlog } = await import('../../services/contentService');
        void Promise.allSettled([
            loadGetCareNowDetails(newLanguage),
            loadHealthBlog(newLanguage),
        ]).then((results) => {
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    const target = index === 0 ? 'GetCareNow' : 'Health Blog';
                    console.error(`Middleware: ${target} preload failed for language: ${newLanguage}`, result.reason);
                }
            });
        });
    },
});
// END: GitHub Copilot Generated Code
